import React, { useState } from 'react';
import theme from '../theme';

const Contact = () => {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      setError('Please fill in all required fields.');
      return;
    }
    // mailto fallback — opens default email client
    const mailtoLink = `mailto:wodgoat@gmail.com?subject=${encodeURIComponent(form.subject || 'Contact from ' + form.name)}&body=${encodeURIComponent(`Name: ${form.name}\nEmail: ${form.email}\n\n${form.message}`)}`;
    window.location.href = mailtoLink;
    setSubmitted(true);
    setError('');
  };

  const styles = {
    page: {
      backgroundColor: theme.colors.background,
      minHeight: '80vh',
    },
    container: {
      maxWidth: '900px',
      margin: '0 auto',
      padding: `${theme.spacing['2xl']} ${theme.spacing.md}`,
    },
    header: {
      textAlign: 'center',
      marginBottom: theme.spacing['2xl'],
    },
    title: {
      fontSize: theme.typography.fontSize['4xl'],
      fontWeight: theme.typography.fontWeight.bold,
      color: theme.colors.text,
      marginBottom: theme.spacing.sm,
      fontFamily: theme.typography.fontFamily.heading,
    },
    subtitle: {
      fontSize: theme.typography.fontSize.lg,
      color: theme.colors.textSecondary,
      lineHeight: theme.typography.lineHeight.relaxed,
      fontFamily: theme.typography.fontFamily.primary,
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
      gap: theme.spacing.xl,
      alignItems: 'start',
    },
    infoSection: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing.md,
    },
    infoCard: {
      padding: theme.spacing.lg,
      backgroundColor: theme.colors.backgroundGray,
      border: `1px solid ${theme.colors.border}`,
      borderRadius: theme.borderRadius.lg,
    },
    infoIcon: {
      fontSize: theme.typography.fontSize['2xl'],
      marginBottom: theme.spacing.xs,
    },
    infoTitle: {
      fontSize: theme.typography.fontSize.base,
      fontWeight: theme.typography.fontWeight.semibold,
      color: theme.colors.text,
      marginBottom: '4px',
      fontFamily: theme.typography.fontFamily.heading,
    },
    infoText: {
      fontSize: theme.typography.fontSize.sm,
      color: theme.colors.textSecondary,
      lineHeight: theme.typography.lineHeight.relaxed,
      fontFamily: theme.typography.fontFamily.primary,
    },
    infoLink: {
      color: theme.colors.primary,
      textDecoration: 'none',
      fontFamily: theme.typography.fontFamily.primary,
      fontSize: theme.typography.fontSize.sm,
    },
    formCard: {
      padding: theme.spacing.xl,
      backgroundColor: theme.colors.backgroundGray,
      border: `1px solid ${theme.colors.border}`,
      borderRadius: theme.borderRadius.lg,
      boxShadow: theme.shadows.lg,
    },
    formTitle: {
      fontSize: theme.typography.fontSize['2xl'],
      fontWeight: theme.typography.fontWeight.bold,
      color: theme.colors.text,
      marginBottom: theme.spacing.lg,
      fontFamily: theme.typography.fontFamily.heading,
    },
    fieldGroup: {
      marginBottom: theme.spacing.md,
    },
    label: {
      display: 'block',
      fontSize: theme.typography.fontSize.sm,
      fontWeight: theme.typography.fontWeight.medium,
      color: theme.colors.textLight,
      marginBottom: '6px',
      fontFamily: theme.typography.fontFamily.primary,
    },
    required: {
      color: theme.colors.primary,
      marginLeft: '2px',
    },
    input: {
      width: '100%',
      padding: `${theme.spacing.sm} ${theme.spacing.md}`,
      backgroundColor: theme.colors.background,
      border: `1px solid ${theme.colors.border}`,
      borderRadius: theme.borderRadius.md,
      color: theme.colors.text,
      fontSize: theme.typography.fontSize.base,
      fontFamily: theme.typography.fontFamily.primary,
      outline: 'none',
      boxSizing: 'border-box',
      transition: theme.transitions.fast,
    },
    textarea: {
      width: '100%',
      padding: `${theme.spacing.sm} ${theme.spacing.md}`,
      backgroundColor: theme.colors.background,
      border: `1px solid ${theme.colors.border}`,
      borderRadius: theme.borderRadius.md,
      color: theme.colors.text,
      fontSize: theme.typography.fontSize.base,
      fontFamily: theme.typography.fontFamily.primary,
      outline: 'none',
      boxSizing: 'border-box',
      resize: 'vertical',
      minHeight: '140px',
      transition: theme.transitions.fast,
    },
    submitButton: {
      width: '100%',
      padding: `${theme.spacing.md} ${theme.spacing.lg}`,
      backgroundColor: theme.colors.primary,
      color: theme.colors.white,
      border: 'none',
      borderRadius: theme.borderRadius.md,
      fontSize: theme.typography.fontSize.lg,
      fontWeight: theme.typography.fontWeight.semibold,
      fontFamily: theme.typography.fontFamily.primary,
      cursor: 'pointer',
      transition: theme.transitions.base,
      marginTop: theme.spacing.xs,
    },
    errorMsg: {
      color: theme.colors.error,
      fontSize: theme.typography.fontSize.sm,
      fontFamily: theme.typography.fontFamily.primary,
      marginBottom: theme.spacing.md,
    },
    successMsg: {
      textAlign: 'center',
      padding: theme.spacing.xl,
    },
    successIcon: {
      fontSize: '3rem',
      marginBottom: theme.spacing.sm,
    },
    successTitle: {
      fontSize: theme.typography.fontSize['2xl'],
      fontWeight: theme.typography.fontWeight.bold,
      color: theme.colors.text,
      marginBottom: theme.spacing.sm,
      fontFamily: theme.typography.fontFamily.heading,
    },
    successText: {
      fontSize: theme.typography.fontSize.base,
      color: theme.colors.textSecondary,
      fontFamily: theme.typography.fontFamily.primary,
    },
  };

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <div style={styles.header}>
          <h1 style={styles.title}>Contact Us</h1>
          <p style={styles.subtitle}>
            Have a question or feedback? We'd love to hear from you.
          </p>
        </div>

        <div style={styles.grid}>
          {/* Info Cards */}
          <div style={styles.infoSection}>
            <div style={styles.infoCard}>
              <div style={styles.infoIcon}>📧</div>
              <div style={styles.infoTitle}>Email</div>
              <a href="mailto:wodgoat@gmail.com" style={styles.infoLink}>
                wodgoat@gmail.com
              </a>
            </div>
            <div style={styles.infoCard}>
              <div style={styles.infoIcon}>🐐</div>
              <div style={styles.infoTitle}>About WODGoat</div>
              <p style={styles.infoText}>
                WODGoat is your AI-powered workout companion. We're always improving — your feedback helps us build a better app.
              </p>
            </div>
            <div style={styles.infoCard}>
              <div style={styles.infoIcon}>⏱️</div>
              <div style={styles.infoTitle}>Response Time</div>
              <p style={styles.infoText}>
                We typically respond within 1–2 business days.
              </p>
            </div>
          </div>

          {/* Contact Form */}
          <div style={styles.formCard}>
            {submitted ? (
              <div style={styles.successMsg}>
                <div style={styles.successIcon}>✅</div>
                <div style={styles.successTitle}>Message Sent!</div>
                <p style={styles.successText}>
                  Thanks for reaching out. We'll get back to you soon.
                </p>
              </div>
            ) : (
              <>
                <h2 style={styles.formTitle}>Send a Message</h2>

                {error && <p style={styles.errorMsg}>{error}</p>}

                <form onSubmit={handleSubmit} noValidate>
                  <div style={styles.fieldGroup}>
                    <label style={styles.label}>
                      Name<span style={styles.required}>*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      placeholder="Your name"
                      style={styles.input}
                      onFocus={(e) => (e.target.style.borderColor = theme.colors.primary)}
                      onBlur={(e) => (e.target.style.borderColor = theme.colors.border)}
                    />
                  </div>

                  <div style={styles.fieldGroup}>
                    <label style={styles.label}>
                      Email<span style={styles.required}>*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      placeholder="you@example.com"
                      style={styles.input}
                      onFocus={(e) => (e.target.style.borderColor = theme.colors.primary)}
                      onBlur={(e) => (e.target.style.borderColor = theme.colors.border)}
                    />
                  </div>

                  <div style={styles.fieldGroup}>
                    <label style={styles.label}>Subject</label>
                    <input
                      type="text"
                      name="subject"
                      value={form.subject}
                      onChange={handleChange}
                      placeholder="What's this about?"
                      style={styles.input}
                      onFocus={(e) => (e.target.style.borderColor = theme.colors.primary)}
                      onBlur={(e) => (e.target.style.borderColor = theme.colors.border)}
                    />
                  </div>

                  <div style={styles.fieldGroup}>
                    <label style={styles.label}>
                      Message<span style={styles.required}>*</span>
                    </label>
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      placeholder="Tell us what's on your mind..."
                      style={styles.textarea}
                      onFocus={(e) => (e.target.style.borderColor = theme.colors.primary)}
                      onBlur={(e) => (e.target.style.borderColor = theme.colors.border)}
                    />
                  </div>

                  <button
                    type="submit"
                    style={styles.submitButton}
                    onMouseOver={(e) => (e.target.style.backgroundColor = theme.colors.primaryDark)}
                    onMouseOut={(e) => (e.target.style.backgroundColor = theme.colors.primary)}
                  >
                    Send Message
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
